<template>
  <div class="kanban-wrapper">
    <div ref="board" class="kanban">
      <Draggable
        v-for="col in allColumns"
        :list="getDraggableItems(col)"
        :group="group"
        class="kanban-column"
        :data-id="col.id"
        :style="`--kanban-column-color: ${col.color}`"
        :animation="200"
        :disabled="!draggable"
        ghost-class="ghost"
        @change="$ev => onChange($ev, col)"
      >
        <div slot="header" class="kanban-column__header">
          <span>{{ getLabel(col) }}</span>
          <Icon
            v-if="col.isFallback"
            icon="fa-pro-light:question-circle"
            v-tooltip="{ content: $t('kanbanLostColumnHelp', { id: col.id }), classes: 'kanban-lost-column-toolip' }"
          />
          <slot name="header" :column="col" :getItems="getItems"></slot>
        </div>
        <div v-for="(item, index) in getItems(col)" :key="item.id" class="kanban-column__item">
          <slot name="item" :item="item" :column="col" :index="index"></slot>
        </div>
      </Draggable>
    </div>

    <div ref="scrollbar" class="kanban-scrollbar" aria-hidden="true" @scroll="onScrollbarScroll">
      <div ref="scrollbarContent" class="kanban-scrollbar__content"></div>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import { Query } from 'mingo'

export const FallbackColumn = { id: 'lost', isFallback: true, label: 'kanbanLostColumnLabel', color: 'orange' }
export const getPipeline = (id, pipelines) => {
  return pipelines
    .sort((a, b) => (b.condition ? 1 : 0) - (a.condition ? 1 : 0))
    .find(pipeline => {
      if (!('condition' in pipeline) || !pipeline.condition) return true

      return new Query(pipeline.condition).test({ id })
    })
}

export default {
  props: {
    items: Array,
    columnKey: String,
    sortKey: String,
    columns: Array,
    draggable: { type: Boolean, default: true },
  },
  provide() {
    return {
      columns: this.columns,
      columnKey: this.columnKey,
      sortKey: this.sortKey,
      getItems: this.getItems,
      getSort: this.getSort,
      getPipeline: this.getPipeline,
    }
  },
  data() {
    return {
      group: Math.random().toString(36).substr(2, 9),
    }
  },
  watch: {
    'allColumns.length'() {
      this.$nextTick(this.measureScrollbar)
    },
  },
  computed: {
    columnIds() {
      return this.columns.map(c => c.id)
    },
    itemsByColumn() {
      const groups = new Map(this.columns.map(c => [c.id, []]))
      const unassigned = []

      this.items.forEach(item => {
        const value = this.$path(this.columnKey, item)
        const group = groups.get(value || this.columnIds[0]) || unassigned

        group.push(item)
      })

      return { groups, unassigned }
    },
    unassigned() {
      return this.itemsByColumn.unassigned
    },
    allColumns() {
      if (this.unassigned.length) return [structuredClone(FallbackColumn), ...this.columns]
      return this.columns
    },
  },
  methods: {
    getLabel(column) {
      if (typeof column.label === 'string') return this.$tc(column.label)
      if (typeof column.label === 'object') return column.label[this.$store.state.user.language]

      return column.label
    },
    getItems(column) {
      const { groups, unassigned } = this.itemsByColumn

      if (!column || column.isFallback) return unassigned
      return groups.get(column.id) || []
    },
    getDraggableItems(column) {
      return this.getItems(column).slice()
    },
    getSort({ column, item, index }) {
      const items = this.getItems(column).filter(i => i.id !== item.id)
      if (index === -1) index = items.length

      const before = items[index - 1]
      const after = items[index]

      if (!before && !after) return this.$path(this.sortKey, item)
      if (!before) return this.$path(this.sortKey, after) - 1
      if (!after) return this.$path(this.sortKey, before) + 1

      return (this.$path(this.sortKey, before) + this.$path(this.sortKey, after)) / 2
    },
    onChange(event, col) {
      const action = Object.keys(event).at(0)
      if (!['added', 'moved'].includes(action)) return

      const target = event[action]

      this.$emit('change', {
        value: col.id,
        target: target.element,
        sort: this.getSort({ column: col, item: target.element, index: target.newIndex }),
      })
    },
    measureScrollbar() {
      const { board, scrollbar, scrollbarContent } = this.$refs
      if (!board) return

      const rect = board.getBoundingClientRect()
      this.boardBox = {
        top: rect.top + window.scrollY,
        height: rect.height,
        scrollable: board.scrollWidth - board.clientWidth > 1,
      }

      scrollbar.style.left = `${rect.left}px`
      scrollbar.style.width = `${board.clientWidth}px`
      scrollbarContent.style.width = `${board.scrollWidth}px`

      this.syncScrollbar()
    },
    syncScrollbar() {
      if (this.scrollbarFrame) return

      this.scrollbarFrame = requestAnimationFrame(() => {
        this.scrollbarFrame = null

        const { board, scrollbar } = this.$refs
        const { top, height, scrollable } = this.boardBox
        const offsetTop = top - window.scrollY

        const visible = scrollable && offsetTop < window.innerHeight && offsetTop + height > window.innerHeight
        scrollbar.classList.toggle('kanban-scrollbar--visible', visible)

        if (visible && scrollbar.scrollLeft !== board.scrollLeft) scrollbar.scrollLeft = board.scrollLeft
      })
    },
    onScrollbarScroll() {
      const { board, scrollbar } = this.$refs
      if (board.scrollLeft !== scrollbar.scrollLeft) board.scrollLeft = scrollbar.scrollLeft
    },
  },
  mounted() {
    this.boardBox = { top: 0, height: 0, scrollable: false }

    window.addEventListener('scroll', this.syncScrollbar, { capture: true, passive: true })
    window.addEventListener('resize', this.measureScrollbar, { passive: true })

    this.resizeObserver = new ResizeObserver(this.measureScrollbar)
    this.resizeObserver.observe(this.$refs.board)

    this.measureScrollbar()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.syncScrollbar, { capture: true })
    window.removeEventListener('resize', this.measureScrollbar)

    this.resizeObserver.disconnect()
    if (this.scrollbarFrame) cancelAnimationFrame(this.scrollbarFrame)
  },
  components: {
    Draggable,
  },
}
</script>

<style lang="scss">
.kanban {
  display: flex;
  gap: 1rem;

  overflow-x: auto;
  overflow-y: visible;

  &-column {
    --kanban-column-padding: 1rem;
    --kanban-column-gap: 1rem;

    width: 320px;
    flex-shrink: 0;

    scroll-margin: 20px;

    background-color: white;
    border-radius: var(--border-radius);

    box-shadow: var(--k-ui-shadow-md);

    min-height: calc(80vh - (var(--kanban-column-padding) * 4));
    overflow-y: auto;

    &:first-child {
      margin-left: auto;
    }

    &:last-child {
      margin-right: auto;
    }

    &__header {
      position: sticky;
      top: 0;
      font-weight: bold;
      padding: var(--kanban-column-padding);
      border-top: 8px solid var(--kanban-column-color);
      border-bottom: 1px solid var(--border-color);

      border-top-left-radius: inherit;
      border-top-right-radius: inherit;

      background-color: inherit;

      display: flex;
      justify-content: space-between;
      align-items: center;

      z-index: 3;
    }

    &__item {
      margin: var(--kanban-column-padding);
    }
  }

  .ghost {
    opacity: 0.2;
  }
}
.kanban-scrollbar {
  display: none;

  position: fixed;
  bottom: 0;
  z-index: 4;

  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;

  background-color: white;
  border-top: 1px solid var(--border-color);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);

  scrollbar-color: rgba(0, 0, 0, 0.35) transparent;

  &--visible {
    display: block;
  }

  &__content {
    height: 1px;
  }

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.35);
    background-clip: content-box;
    border: 3px solid transparent;
    border-radius: 50px;

    &:hover {
      background: rgba(0, 0, 0, 0.55);
      background-clip: content-box;
    }
  }
}
.kanban-lost-column-toolip {
  max-width: 32ch;
}
</style>
